import { supabase } from '../config/supabase';
import { cacheService } from './cacheService';

class ProductService {
  // Cache keys
  static CACHE_KEYS = {
    ALL_PRODUCTS: 'products_all',
    CATEGORIES: 'categories_all',
    PRODUCT_BY_ID: 'product_',
    PRODUCTS_BY_CATEGORY: 'products_category_'
  };

  // Get all products with caching
  static async getAllProducts() {
    const cacheKey = this.CACHE_KEYS.ALL_PRODUCTS;
    const cached = cacheService.get(cacheKey);
    
    if (cached) {
      return { data: cached, error: null, fromCache: true };
    }

    try {
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          categories(name, description)
        `);

      if (!error && data) {
        // Cache for 10 minutes
        cacheService.set(cacheKey, data, 10 * 60 * 1000);
      }

      return { data, error, fromCache: false };
    } catch (err) {
      return { data: null, error: err, fromCache: false };
    }
  }

  // Get categories with caching
  static async getCategories() {
    const cacheKey = this.CACHE_KEYS.CATEGORIES;
    const cached = cacheService.get(cacheKey);
    
    if (cached) {
      return { data: cached, error: null, fromCache: true };
    }

    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*");

      if (!error && data) {
        // Cache for 30 minutes (categories change less frequently)
        cacheService.set(cacheKey, data, 30 * 60 * 1000);
      }

      return { data, error, fromCache: false };
    } catch (err) {
      return { data: null, error: err, fromCache: false };
    }
  }

  // Get product by ID with caching
  static async getProductById(id) {
    const cacheKey = this.CACHE_KEYS.PRODUCT_BY_ID + id;
    const cached = cacheService.get(cacheKey);
    
    if (cached) {
      return { data: cached, error: null, fromCache: true };
    }

    try {
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          categories(name, description)
        `)
        .eq('id', id)
        .single();

      if (!error && data) {
        // Cache for 15 minutes
        cacheService.set(cacheKey, data, 15 * 60 * 1000);
      }

      return { data, error, fromCache: false };
    } catch (err) {
      return { data: null, error: err, fromCache: false };
    }
  }

  // Batch load products and categories
  static async loadInitialData() {
    try {
      const [productsResult, categoriesResult] = await cacheService.batchRequests([
        this.getAllProducts(),
        this.getCategories()
      ]);

      return {
        products: productsResult?.data || [],
        categories: categoriesResult?.data || [],
        errors: {
          products: productsResult?.error,
          categories: categoriesResult?.error
        }
      };
    } catch (err) {
      return {
        products: [],
        categories: [],
        errors: { general: err }
      };
    }
  }

  // Get products with options (for backward compatibility)
  static async getProducts(options = {}) {
    const { limit, category, ...filters } = options;
    
    try {
      let query = supabase
        .from("products")
        .select(`
          *,
          categories(name, description)
        `);

      // Apply filters
      if (category) {
        query = query.eq('categories.name', category);
      }

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Upload product image
  static async uploadProductImage(file, path) {
    try {
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(path, file);

      if (error) {
        return { url: null, error };
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(path);

      return { url: publicUrl, error: null };
    } catch (err) {
      return { url: null, error: err };
    }
  }

  // Check product availability
  static async checkProductAvailability(productId, quantity) {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('in_stock, stock_quantity')
        .eq('id', productId)
        .single();

      if (error) {
        return { data: false, error };
      }

      const isAvailable = data.in_stock && (data.stock_quantity >= quantity);
      return { data: isAvailable, error: null };
    } catch (err) {
      return { data: false, error: err };
    }
  }

  // Update product stock
  static async updateProductStock(productId, quantity, isRestore = false) {
    try {
      // Get current stock
      const { data: product, error: fetchError } = await supabase
        .from('products')
        .select('stock_quantity')
        .eq('id', productId)
        .single();

      if (fetchError) {
        return { error: fetchError };
      }

      // Calculate new stock
      const newStock = isRestore 
        ? product.stock_quantity + quantity 
        : product.stock_quantity - quantity;

      // Update stock
      const { error: updateError } = await supabase
        .from('products')
        .update({ 
          stock_quantity: Math.max(0, newStock),
          in_stock: newStock > 0
        })
        .eq('id', productId);

      return { error: updateError };
    } catch (err) {
      return { error: err };
    }
  }

  // Clear cache when needed (e.g., after product updates)
  static clearCache() {
    cacheService.clear();
  }
}

export default ProductService;